/**
 * EditorialRiskMatrix — the classic likelihood × impact 3×3 grid with
 * status-tinted cells and risk chips placed into the cell their
 * (likelihood, impact) coordinate selects.
 *
 * Editorial reference: `.matrix` in
 *   docs/design/round3-sentiment-risk-pathway.html (Risk 14A):
 *     a 3×3 grid; cells washed lo/md/hi (go/cond/nogo at low alpha);
 *     each risk a mono chip on a paper cell with a hairline-strong border.
 *     Y axis (impact) labels rotated; X axis (likelihood) labels beneath.
 *
 * Used by:
 *   - Risk Assessment (14A)              — risks by likelihood × impact
 *   - BTM Technology (5B, via the marks) — criterion × technology fit
 *     (BTM 5B is better served by EditorialDataTable with mark glyphs;
 *      this primitive owns the true 2-axis scatter-into-cells grid.)
 *
 * Cells are tinted by their SEVERITY band (low/medium/high), computed
 * from the (likelihood, impact) ordinal sum so the upper-right cluster
 * reads hot. Theme tokens read: 'positive'/'warning'/'negative' (low/med/
 * high cell washes via roleColorAlpha), 'hair' (cell rules), 'rule'
 * (chip border), 'ink' (chip ink), 'muted' (axis labels), 'faint' (axis
 * caption). Mono labels via --report-body-font. Pure / print-safe.
 */
import React from 'react';
import type { ReportTheme } from '../../../../types/reportTheme';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { REPORT_TEXT_PX } from '../../../../lib/reportSectionHeading';

const MONO = 'var(--report-body-font, inherit)';

/** 1 = low … 3 = high, on each axis. */
export type RiskLevel = 1 | 2 | 3;

export interface EditorialRiskItem {
  label: React.ReactNode;
  likelihood: RiskLevel;
  impact: RiskLevel;
}

export interface EditorialRiskMatrixProps {
  theme: ReportTheme;
  items: EditorialRiskItem[];
  /** X-axis (likelihood) tick labels, low→high. Defaults Low/Medium/High. */
  likelihoodLabels?: [React.ReactNode, React.ReactNode, React.ReactNode];
  /** Y-axis (impact) tick labels, low→high. Defaults Low/Med/High impact. */
  impactLabels?: [React.ReactNode, React.ReactNode, React.ReactNode];
  id?: string;
  className?: string;
  style?: React.CSSProperties;
}

type Band = 'low' | 'medium' | 'high';

/** Severity band from the (likelihood + impact) ordinal sum (2..6). */
function bandFor(likelihood: RiskLevel, impact: RiskLevel): Band {
  const sum = likelihood + impact;
  if (sum >= 5) return 'high';
  if (sum >= 4) return 'medium';
  return 'low';
}

export function EditorialRiskMatrix({
  theme,
  items,
  likelihoodLabels = ['Low', 'Medium', 'High'],
  impactLabels = ['Low impact', 'Med impact', 'High impact'],
  id,
  className,
  style,
}: EditorialRiskMatrixProps): React.ReactElement | null {
  if (items.length === 0) return null;

  const hair = roleColor(theme, 'hair');
  const ruleStrong = roleColor(theme, 'rule');
  const ink = roleColor(theme, 'ink');
  const muted = roleColor(theme, 'muted');
  const faint = roleColor(theme, 'faint');

  const cellWash = (band: Band): string => {
    if (band === 'high') return roleColorAlpha(theme, 'negative', 0.11);
    if (band === 'medium') return roleColorAlpha(theme, 'warning', 0.1);
    return roleColorAlpha(theme, 'positive', 0.09);
  };

  // Build a 3 (impact rows, high→low) × 3 (likelihood cols, low→high) grid.
  const rowsImpact: RiskLevel[] = [3, 2, 1];
  const colsLikelihood: RiskLevel[] = [1, 2, 3];

  const itemsAt = (impact: RiskLevel, likelihood: RiskLevel): EditorialRiskItem[] =>
    items.filter((it) => it.impact === impact && it.likelihood === likelihood);

  return (
    <div id={id} className={className} style={style}>
      <div
        role="img"
        aria-label="Risk likelihood by impact matrix"
        style={{
          display: 'grid',
          gridTemplateColumns: `54px repeat(3, minmax(0, 1fr))`,
          gridAutoRows: 64,
        }}
      >
        {rowsImpact.map((impact, r) => (
          <React.Fragment key={`row-${impact}`}>
            <div
              style={{
                fontFamily: MONO,
                fontSize: REPORT_TEXT_PX.micro,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: muted,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                writingMode: 'vertical-rl',
                transform: 'rotate(180deg)',
              }}
            >
              {impactLabels[impact - 1]}
            </div>
            {colsLikelihood.map((likelihood) => {
              const band = bandFor(likelihood, impact);
              const cellItems = itemsAt(impact, likelihood);
              return (
                <div
                  key={`cell-${impact}-${likelihood}`}
                  style={{
                    border: `1px solid ${hair}`,
                    background: cellWash(band),
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 4,
                    alignContent: 'center',
                    justifyContent: 'center',
                    padding: 5,
                  }}
                >
                  {cellItems.map((it, k) => (
                    <span
                      key={k}
                      style={{
                        fontFamily: MONO,
                        fontSize: 8.5,
                        letterSpacing: '0.02em',
                        color: ink,
                        border: `1px solid ${ruleStrong}`,
                        padding: '2px 5px',
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {it.label}
                    </span>
                  ))}
                </div>
              );
            })}
            {r === rowsImpact.length - 1 && null}
          </React.Fragment>
        ))}
      </div>
      {/* X-axis tick labels */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `54px repeat(3, minmax(0, 1fr))`,
        }}
      >
        <div />
        {colsLikelihood.map((likelihood) => (
          <div
            key={`xlab-${likelihood}`}
            style={{
              fontFamily: MONO,
              fontSize: REPORT_TEXT_PX.micro,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: muted,
              textAlign: 'center',
              paddingTop: 6,
            }}
          >
            {likelihoodLabels[likelihood - 1]}
          </div>
        ))}
      </div>
      <div
        style={{
          fontFamily: MONO,
          fontSize: 8.5,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: faint,
          textAlign: 'center',
          marginTop: 4,
        }}
      >
        Likelihood →
      </div>
    </div>
  );
}
